import {EventEmitter, Injectable} from '@angular/core';
import {CustomerControllerService, CustomerRequest} from "skyhopper-api";
import {ValidationDetail} from "../models/ValidationDetail";
import {BehaviorSubject, Observable} from "rxjs";
import {ConfirmedCustomers} from "../../core/models/nao/confirmed-customers";
import {Router} from "@angular/router";
import {
  ApplicantRequest, ApplicationLogsControllerService,
  ApplicationLogsRequest,
} from "leia-api";
import {Customer} from "../../core/models/nao/customer";
import {SessionStorageService} from "../models/session-storage.service";
import {FSR_RM_ASSISTED, NAO_APPLICATION_LOG_REQUEST_KEY, NAO_PRODUCT_SELECTION_ROUTE} from "../constants";
import {ApplicationService} from "../../application/application.service";
import {UtilitiesService} from "../../core/services/utilities.service";
import {ApplicationType} from "../models/application-type.enum";
import {NGXLogger} from "ngx-logger";
import {MatSnackBar} from "@angular/material/snack-bar";


@Injectable({
  providedIn: 'root'
})

export class NaoService {
  public confirmedCustomers!: ConfirmedCustomers;
  public cifCreateRequest: CustomerRequest = new CustomerRequest();
  public validationsChange = new EventEmitter<ValidationDetail>();
  public onTriggerValidations = new EventEmitter<void>();
  public showCreateButton$ = new BehaviorSubject<boolean>(true);
  public confirmedCustomersHasChanged$ = new BehaviorSubject<boolean>(false);
  public isCreateBeneficiaryRoute$ = new BehaviorSubject<boolean>(false);
  public maxApplicantsAdded$ = new BehaviorSubject<boolean>(false);
  public cifToApplicantMap = new Map<number, CustomerRequest>();
  public createApplicationComplete$ = new BehaviorSubject<boolean>(false);
  id: string = '';

  /**
   * Constructor
   */
  constructor(
    private router: Router,
    private customerControllerService: CustomerControllerService,
    private sessionStorageService: SessionStorageService,
    private applicationService: ApplicationService,
    private utils: UtilitiesService,
    private leiaService: ApplicationLogsControllerService,
    private logger: NGXLogger,
    private snackBar: MatSnackBar,
  ) {
  }

  /**
   * Get session id
   */
  get sessionId(): string {
    return this.id;
  }

  /**
   * Set session id
   */
  set sessionId(sessionId: string) {
    this.id = sessionId;
  }

  /**
   * Set the applicationLogRequest
   * @param request
   */
  set applicationLogsRequest(request: ApplicationLogsRequest) {
    this.sessionStorageService.set(NAO_APPLICATION_LOG_REQUEST_KEY, request);
  }

  /**
   * Get applicationLogRequest
   */
  get applicationLogsRequest(): ApplicationLogsRequest {
    let applicationLog = this.sessionStorageService.get(NAO_APPLICATION_LOG_REQUEST_KEY);

    if (!applicationLog) {
      applicationLog = <ApplicationLogsRequest>{};
      this.applicationLogsRequest = applicationLog;
    }

    return applicationLog;
  }

  /**
   * Initiates the create customer api
   */
  public createCif(): Promise<any> {
    return this.customerControllerService.createCustomer(this.cifCreateRequest).toPromise();
  }

  /**
   * Initiates the customer search api with the given request.
   * @param request
   */
  public getSearchResults(request: any): Observable<any> {
    return this.customerControllerService.getCustomerProfileForSearch(request);
  }

  /**
   * Resets the customer request
   */
  public resetCustomerRequest() {
    this.cifCreateRequest = new CustomerRequest();
    this.maxApplicantsAdded$.next(false);
  }

  /**
   * Resets the logic for showing the create customer button
   * @param sessionId Session ID to lookup application.
   */
  public resetControls() {
    this.showCreateButton$.next(true);
  }

  /**
   * Creates an applicationLogRequest and maps the confirmedCustomers to necessary applicantRequest.
   */
  public createApplication() {
    const confirmedCustomers = this.confirmedCustomers.customers;

    if (confirmedCustomers.length < 0) {
      return;
    }

    let applicationRequest = this.applicationLogsRequest;

    applicationRequest.status = 'Created';
    applicationRequest.applicationChannelId = FSR_RM_ASSISTED;
    applicationRequest.applicationType = ApplicationType.NewClientApplication;
    applicationRequest.isJoint = 0;

    let applicants: Array<ApplicantRequest> = [];

    confirmedCustomers.forEach((customer: Customer) => {
      let applicantRequest = new ApplicantRequest();
      applicantRequest.cif = customer.cif;
      applicantRequest.ssn = customer.ssn;
      applicantRequest.dateOfBirth = customer.birthdate;
      let firstName = customer.firstName;
      let lastName = customer.lastName;
      applicantRequest.firstName = firstName;
      applicantRequest.lastName = lastName;
      applicants.push(applicantRequest);
    });

    applicationRequest.applicants = applicants;
    this.applicationLogsRequest = applicationRequest;
    this.submitApplication();
  }

  /**
   * Calls the Leia service startSession with the build applicationLogRequest
   */
  private submitApplication() {
    const applicationLogRequest = this.applicationLogsRequest;

    this.leiaService.startSession(applicationLogRequest).subscribe(async (response: any) => {
      let sessionId = response.toString();
      applicationLogRequest.sessionId = sessionId;
      this.sessionId = sessionId;
      this.applicationLogsRequest = applicationLogRequest;
      await this.router.navigate([NAO_PRODUCT_SELECTION_ROUTE]);
      this.createApplicationComplete$.next(true);
    }, (error: any) => {
      this.logger.error("Failed to submit application - " + error);
      this.onError();
    });
  }

  /**
   * Error method for submitApplication
   */
  private onError = () => {
    let snackbarRef = this.snackBar.open("Could not create NAO application", "Dismiss");
    snackbarRef.onAction().subscribe(() => {
      this.snackBar.dismiss();
    });
  }
}
